import React, { useCallback, useEffect, useState, useRef, lazy } from 'react';
import { ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import { FadeIn } from './FadeIn';
type Slide = {
  src: string;
  alt: string;
  label: string;
  title: string;
  desc: string;
};
const HERO_IMAGE_URL = 'https://cdn.magicpatterns.com/uploads/7BkzCbELLsUwyvco8cmoUC/Texto_del_parrafo.webp';
const slides: Slide[] = [{
  src: HERO_IMAGE_URL,
  alt: 'Módulo 4 — Fórmulas de descuento de cristal',
  label: 'MÓDULO 4 · Cristales',
  title: 'Fórmulas de Descuento de Cristal',
  desc: 'Tablas exactas para cada sistema. Metés el vano y sabés el cristal exacto. Sin cálculos de cabeza.'
}, {
  src: HERO_IMAGE_URL,
  alt: 'Módulo 6 — Sellado en capas',
  label: 'MÓDULO 6 · Instalación',
  title: 'Protocolo de Sellado en 4 Capas',
  desc: 'Orden exacto: cinta butilénica, espuma PU, silicona exterior, silicona interior. Cero filtraciones.'
}, {
  src: HERO_IMAGE_URL,
  alt: 'Módulo 3 — Sistemas corredizos',
  label: 'MÓDULO 3 · Corredizos',
  title: 'Armado de Sistema Corredizo Paso a Paso',
  desc: 'Kits estándar, perfiles, herrajes y ajuste fino. La secuencia exacta para que el ayudante trabaje solo.'
}, {
  src: HERO_IMAGE_URL,
  alt: 'Módulo 2 — Protocolo de medición',
  label: 'MÓDULO 2 · Medición',
  title: 'Protocolo de 3 Puntos de Medición',
  desc: 'Plomo, escuadra y vanos críticos. Cómo medir bien desde el primer intento, sin volver dos veces.'
}, {
  src: HERO_IMAGE_URL,
  alt: 'Módulo 8 — Presupuestos con margen',
  label: 'MÓDULO 8 · Negocio',
  title: 'Calculadora de Presupuesto con Margen Real',
  desc: 'Los 6 componentes del costo, el precio de venta con margen garantizado y cómo defenderlo.'
}];
export function ContentPreview() {
  const [current, setCurrent] = useState(0);
  const [imagesReady, setImagesReady] = useState(false);
  const sectionRef = useRef<HTMLElement | null>(null);
  const total = slides.length;
  const touchStartX = useRef<number | null>(null);
  const touchDeltaX = useRef(0);
  const goTo = useCallback((i: number) => {
    setCurrent((i % total + total) % total);
  }, [total]);
  const next = useCallback(() => goTo(current + 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1), [current, goTo]);
  const onKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      next();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      prev();
    }
  };
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (touchStartX.current == null) return;
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
  };
  const onTouchEnd = () => {
    if (Math.abs(touchDeltaX.current) > 50) {
      if (touchDeltaX.current < 0) next();else prev();
    }
    touchStartX.current = null;
    touchDeltaX.current = 0;
  };
  useEffect(() => {
    if (imagesReady) return;
    const el = sectionRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setImagesReady(true);
      return;
    }
    const io = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) {
        setImagesReady(true);
        io.disconnect();
      }
    }, {
      rootMargin: '300px'
    });
    io.observe(el);
    return () => io.disconnect();
  }, [imagesReady]);
  const shouldLoad = (i: number) => {
    if (!imagesReady) return false;
    if (i === current) return true;
    if (current !== 0) {
      if (i === (current + 1) % total) return true;
      if (i === (current - 1 + total) % total) return true;
    }
    return false;
  };
  const slide = slides[current];
  return <section ref={sectionRef} className="py-12 md:py-16 bg-background border-y border-white/5" aria-label="Vista previa del contenido del sistema" data-id="element-39">
      <div className="container mx-auto px-4 max-w-6xl" data-id="element-40">
        <FadeIn data-id="element-41">
          <div className="text-center mb-10" data-id="element-42">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-4 rounded-full border border-gold/40 bg-gold/10" data-id="element-43">
              <Eye className="w-3.5 h-3.5 text-gold" aria-hidden="true" data-id="element-44" />
              <span className="text-gold text-xs font-semibold uppercase tracking-wider" data-id="element-45">
                Mirá adentro del sistema
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" data-id="element-46">
              Esto es lo que vas a encontrar adentro.
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto" data-id="element-47">
              Fórmulas reales, no relleno. Cada módulo trae material técnico
              listo para usar en el taller y en la obra.
            </p>
          </div>
        </FadeIn>

        <FadeIn delay={0.1} data-id="element-48">
          <div className="relative" role="region" aria-roledescription="carrusel" aria-label="Vista previa de páginas del sistema" tabIndex={0} onKeyDown={onKey} data-id="element-49">
            <div className="grid md:grid-cols-[1fr_320px] gap-6 md:gap-8 items-stretch" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} data-id="element-50">
              <div className="relative bg-surface rounded-2xl border border-white/10 overflow-hidden" data-id="element-51">
                <div className="relative w-full aspect-[3/4] md:aspect-[4/5] bg-navy-dark" data-id="element-52">
                  {slides.map((s, i) => <img key={i} src={shouldLoad(i) ? s.src : undefined} alt={s.alt} width={800} height={1067} loading="lazy" decoding="async"
                // @ts-ignore
                fetchpriority={i === current ? 'high' : 'low'} className={`absolute inset-0 w-full h-full object-contain p-4 md:p-6 transition-opacity duration-500 ease-out ${i === current ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} aria-hidden={i !== current} data-id="element-53" />)}
                </div>

                <button type="button" onClick={prev} aria-label="Vista previa anterior" className="absolute top-1/2 left-2 md:left-3 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-background/80 backdrop-blur border border-white/10 text-white flex items-center justify-center hover:bg-gold hover:text-background hover:border-gold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold" data-id="element-54">
                  <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" data-id="element-55" />
                </button>
                <button type="button" onClick={next} aria-label="Siguiente vista previa" className="absolute top-1/2 right-2 md:right-3 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-background/80 backdrop-blur border border-white/10 text-white flex items-center justify-center hover:bg-gold hover:text-background hover:border-gold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold" data-id="element-56">
                  <ChevronRight className="w-5 h-5 md:w-6 md:h-6" data-id="element-57" />
                </button>

                <div className="absolute top-3 right-3 bg-background/80 backdrop-blur px-3 py-1 rounded-full border border-white/10 text-xs text-gray-300 font-medium" data-id="element-58">
                  {current + 1} / {total}
                </div>
              </div>

              <div className="bg-surface rounded-2xl border border-white/10 p-6 flex flex-col justify-between" aria-live="polite" data-id="element-59">
                <div data-id="element-60">
                  <span className="inline-block text-[10px] font-bold text-gold uppercase tracking-widest border border-gold/40 bg-gold/10 px-2 py-1 rounded-md mb-3" data-id="element-61">
                    {slide.label}
                  </span>
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-3 leading-tight" data-id="element-62">
                    {slide.title}
                  </h3>
                  <p className="text-gray-400 text-sm md:text-base leading-relaxed" data-id="element-63">
                    {slide.desc}
                  </p>
                </div>

                <div className="mt-6 pt-6 border-t border-white/5" data-id="element-64">
                  <p className="text-xs text-gray-500 mb-3" data-id="element-65">
                    Navegá las páginas:
                  </p>
                  <div className="flex items-center gap-2 flex-wrap" role="tablist" aria-label="Seleccionar página de vista previa" data-id="element-66">
                    {slides.map((_, i) => <button key={i} type="button" role="tab" aria-selected={i === current} aria-label={`Vista previa ${i + 1} de ${total}`} onClick={() => goTo(i)} className={`h-2 rounded-full transition-all duration-300 ${i === current ? 'w-8 bg-gold' : 'w-2 bg-white/20 hover:bg-white/40'}`} data-id="element-67" />)}
                  </div>
                </div>
              </div>
            </div>

            <p className="md:hidden text-center text-xs text-gray-500 mt-4" data-id="element-68">
              👉 Deslizá para ver más páginas
            </p>
          </div>
        </FadeIn>

        <FadeIn delay={0.3} data-id="element-69">
          <div className="mt-10 text-center" data-id="element-70">
            <p className="text-gray-400 text-sm md:text-base" data-id="element-71">
              <span className="text-gold font-semibold" data-id="element-72">220+ páginas</span> de
              fórmulas, planillas y protocolos como estos. Todo listo para
              imprimir y llevar al taller.
            </p>
          </div>
        </FadeIn>
      </div>
    </section>;
}